import moment from 'moment';
import _ from 'lodash';
import * as Query from './sequelizeFormulaParser';
import { assetTypes } from '@/services/config/constant.service';

export const getExtraInfoFromAssets = (pool, assets) => {
  const extraInfo = getExtraInfoFromLoans(
    getEligibleAssetsInPool(pool, assets || [])
  );
  return extraInfo;
};

export const getExtraInfoFromLoans = assets => {
  let outstandingAmount = 0;
  let totalAPY = 0;
  let totalTermInDays = 0;
  let assetCount = 0;
  let outstandingAssetCount = 0;

  const balances = new Array(assets.length).fill(0);

  assets.forEach((asset, idx) => {
    const isSubmittedAsset = !!asset.id;
    const maturityDate = _.get(
      asset,
      'debtOrderContract.debtOrderContractInfo.finalMaturityDate'
    );
    const financingDate = _.get(asset, 'createdAt');

    const termInDays = moment
      .unix(+maturityDate / 1000)
      .diff(moment.unix(+financingDate / 1000), 'days');

    if (isSubmittedAsset) {
      const isTokenizedAsset = !!asset.tokenId;
      if (isTokenizedAsset) {
        assetCount++;
        balances[idx] = +asset.outstandingPrincipalAmount;
        outstandingAmount += balances[idx];

        if (balances[idx]) {
          outstandingAssetCount++;
          totalAPY +=
            asset.interestRatePercentage * +asset.outstandingPrincipalAmount;
        }
      }
    } else {
      assetCount++;
      outstandingAmount += +asset.outstandingPrincipalAmount;
      totalAPY += +asset.interestRate * +asset.outstandingPrincipalAmount;
      balances[idx] = +asset.outstandingPrincipalAmount;
    }

    totalTermInDays += termInDays * +asset.outstandingPrincipalAmount;
  });

  outstandingAmount = _.round(outstandingAmount, 2);

  balances.sort((a, b) => b - a);
  let balance = 0;
  let count = 0;
  while (count < 5 && balances[count]) {
    balance += balances[count];
    count++;
  }

  const topFiveConcentration = _.round((balance / outstandingAmount) * 100, 2);
  const averageOutstandingAmount = !assetCount
    ? 0
    : _.round(outstandingAmount / assetCount, 2);

  const weightedAverageAPY = !outstandingAmount
    ? 0
    : _.round(totalAPY / outstandingAmount, 2);

  const averageTerm = !outstandingAmount
    ? 0
    : _.round(totalTermInDays / outstandingAmount, 2);

  return {
    outstandingAmount,
    averageOutstandingAmount,
    weightedAverageAPY,
    averageTerm,
    topFiveConcentration,
  };
};

export const getExtraInfoFromInvoices = (assets = []) => {
  let totalAmount = 0;
  let totalTerm = 0;
  let assetCount = 0;
  const amountArr = new Array(assets.length).fill(0);

  assets.forEach((asset: any, idx) => {
    const isSubmittedAsset = !!asset.id;
    if (isSubmittedAsset) {
      const isTokenizedAsset = !!asset.aitTokenId;
      if (isTokenizedAsset) {
        assetCount++;

        amountArr[idx] = asset.outstandingAmount;
        totalAmount += amountArr[idx];
        if (amountArr[idx])
          totalTerm += moment(asset.paymentDueDate).diff(
            moment(asset.issueDate),
            'months'
          );
      }
    } else {
      assetCount++;

      amountArr[idx] = asset.grossAmount
        ? +asset.grossAmount
        : +asset.netAmount + +asset.taxAmount;
      totalAmount += amountArr[idx];
      totalTerm += moment(asset.invoiceDueDate).diff(
        moment(asset.invoiceDate),
        'months'
      );
    }
  });

  totalAmount = _.round(totalAmount, 2);

  let balance = 0;
  let count = 0;
  amountArr.sort((a, b) => b - a);
  while (count < 5 && amountArr[count]) {
    balance += amountArr[count];
    count++;
  }

  const topFiveConcentration = _.round((balance / totalAmount) * 100, 2);
  const averageAmount = !assetCount ? 0 : _.round(totalAmount / assetCount, 2);
  const averageTerm = !assetCount ? 0 : _.round(totalTerm / assetCount, 2);

  return {
    outstandingAmount: totalAmount,
    averageOutstandingAmount: averageAmount,
    averageTerm,
    topFiveConcentration,
  };
};

export const getExtraInfoFromNoteTokenAssets = (assets = []) => {
  let outstandingAmount = 0;
  let totalAPY = 0;
  let totalTerm = 0;
  let assetCount = 0;
  const balances = new Array(assets.length).fill(0);
  assets.forEach((asset: any, idx) => {
    assetCount++;
    if (asset.principal) {
      outstandingAmount += asset.principal + asset.interest;
      totalAPY += asset.poolEvent.apy;
      totalTerm += asset.investmentTerms / 30;
      balances[idx] = asset.principal;
    } else {
      balances[idx] = 0;
    }
  });

  outstandingAmount = _.round(outstandingAmount, 2);

  balances.sort((a, b) => b - a);
  let balance = 0;
  let count = 0;
  while (count < 5 && balances[count]) {
    balance += balances[count];
    count++;
  }

  const topFiveConcentration =
    outstandingAmount > 0 ? _.round((balance / outstandingAmount) * 100, 2) : 0;
  const averageOutstandingAmount = !assetCount
    ? 0
    : _.round(outstandingAmount / assetCount, 2);
  const weightedAverageAPY = !assetCount
    ? 0
    : _.round(totalAPY / assetCount, 2);
  const averageTerm = !assetCount ? 0 : _.round(totalTerm / assetCount, 2);

  return {
    outstandingAmount,
    averageOutstandingAmount,
    weightedAverageAPY,
    averageTerm,
    topFiveConcentration,
  };
};

export const getEligibleAssetsInPool = (pool, assets) => {
  try {
    const filter = [
      ..._.get(pool, 'ineligibleFilter', []),
      ..._.get(pool, 'reserveFilter', []),
    ];
    return Query.query(
      assets ||
        (_.get(pool, 'assetType') === assetTypes.LENDING
          ? pool.assets
          : pool.invoiceAssets),
      filter.length
        ? {
            $not: filter.map(i => {
              return i.poolFilterConditions;
            }),
          }
        : {}
    );
  } catch (err) {
    return null;
  }
};
